import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { FreeShippingDelivery as FreeShippingAndReturnSchema } from '@amplience/content-types/typings/c-shipping-section';
import type { DeliveryOption } from '../../../codegen/types';
import { useMasterLayoutContext } from '../../../context/masterLayoutContext';
import { ArrowDown } from '../../../icons';
import { transformTextToId } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { DeliveryOptionsList } from '../deliveryOptionsList/DeliveryOptionsList';
import {
  AccordionContainer,
  AccordionHeading,
  Container,
  DeliveryAccordion,
  DeliveryLabel,
  IconWrapper,
  ShippingSection,
} from './DeliveryOptions.styles';

type DeliveryOptionsProps = {
  deliveryOptions: DeliveryOption[];
  isDeliveryOptionsLoading: boolean;
};

export function DeliveryOptions({ deliveryOptions, isDeliveryOptionsLoading }: DeliveryOptionsProps) {
  const { t } = useTranslation(['lib-global-common']);
  const { masterLayoutData } = useMasterLayoutContext();
  const { freeShippingAndReturn = null } = { ...masterLayoutData };

  const router = useRouter();
  const isPaymentPage = router.asPath.includes('/checkout/payment');
  const isConfirmationPage = router.asPath.includes('/checkout/confirmation');

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const toggleDeliveryAccordion = () => {
    setIsAccordionOpen((prev) => !prev);
  };
  const deliveryLabel = t('cart.delivery.and.returns');

  if (isPaymentPage || isConfirmationPage) return null;

  return (
    <Container>
      <AccordionHeading>
        <DeliveryAccordion
          type="button"
          onClick={toggleDeliveryAccordion}
          aria-expanded={isAccordionOpen}
          aria-controls={`accordion-container-${transformTextToId(deliveryLabel)}`}
          aria-label={deliveryLabel}
        >
          <DeliveryLabel>{deliveryLabel}</DeliveryLabel>
          <IconWrapper $isOpenDelivery={isAccordionOpen}>
            <ArrowDown className="accordion-arrow-down" width={25} aria-hidden alt="" viewBox="0 0 20 20" />
          </IconWrapper>
        </DeliveryAccordion>
      </AccordionHeading>
      <AccordionContainer
        $isOpenDelivery={isAccordionOpen}
        id={`accordion-container-${transformTextToId(deliveryLabel)}`}
        hidden={!isAccordionOpen}
        aria-hidden={!isAccordionOpen}
      >
        <>
          {isDeliveryOptionsLoading ? <Loader isDark /> : <DeliveryOptionsList deliveryOptions={deliveryOptions} />}
          {freeShippingAndReturn && (
            <ShippingSection {...(freeShippingAndReturn as unknown as FreeShippingAndReturnSchema)} />
          )}
        </>
      </AccordionContainer>
    </Container>
  );
}
